/**
 * Lab `/bet-ticker` + `/bet-ticker-previous` + `/bet-ticker-flag-final`.
 * Chrome matches Strix Backend-UI Book Management ticker (FilterBar desk layout).
 */

import {
  Button,
  Chip,
  DataTable,
  DateRangePicker,
  Dot,
  Drawer,
  Field,
  FilterBar,
  Icon,
  Modal,
  PageHead,
  Pager,
  Segmented,
  StateBlock,
  Textarea,
  resolveRangePreset,
  useToast,
  type ActiveFilter,
  type Column,
  type DateRangePresetId,
} from '@w7/ui'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useBookTicker,
  useBookTickerFilterOptions,
  useFlagBet,
} from '@/api/hooks/bet-ticker/useBookTicker'
import { useFraudScores } from '@/api/hooks/bet-ticker/useFraudScores'
import {
  resolveTickerMemberFlag,
  TICKER_FLAG_COLORS,
  useBettickerMapping,
} from '@/api/hooks/bet-ticker/useSmartMembers'
import {
  BETFAIR_REASON_LEGEND,
  betfairReasonDotClass,
  betfairReasonRowClass,
  showBetfairReasonDot,
} from '@/features/bet-ticker/betfairReason'
import { useCmMe } from '@/api/hooks/useCmMe'
import { useAllTm } from '@/api/hooks/reports/useAllTm'
import {
  canCommentTickerBet,
  canExportTickerExcel,
  canFlagTickerBet,
  canManageMembersIpMapping,
  canManageMembersMapping,
  canManageSmartMembers,
  type TickerAccessPage,
} from '@/app/staffAccess'
import {
  buildExcelGenerateRequest,
  useTickerExcelGenerate,
} from '@/api/hooks/bet-ticker/useTickerExcel'
import { ago, hms, indianCurrency, ll, lll, odds, tickerAmt } from '@/lib/format'
import { formatLineSessionDisplayRun } from '@/lib/lineSessionDisplayRun'
import { isLineSessionBetListMarket } from '@/features/net-exposure/workstation/lineSessionBetOdds'
import {
  BETFAIR_RANGE_MAX_DAYS,
  DEFAULT_TICKER_FILTERS,
  PREVIOUS_RANGE_MAX_DAYS,
  TICKER_PAGE_SIZES,
  defaultFlagFinalFilters,
  defaultPreviousRange,
  shouldUseTickerBq,
  tickerStatusLabel,
  tickerStatusesForScope,
  type FinalFlagCategory,
  type TickerBet,
  type TickerFilters,
} from './domain'
import { FlagCell } from './FlagCell'
import {
  FLAG_COMMENT_MAX,
  flagClearFinalRequest,
  flagCommentRequest,
  flagFinalCategoryRequest,
  flagToggleRequest,
  isFlagCommentReady,
  normalizeFlagComment,
} from './flagBet'
import {
  formatPlayerScore,
  trustIndicatorTone,
  uniqueUsernames,
} from './fraudScore'
import { marketsMatchingType } from './marketType'
import {
  filtersAreActive,
  orderStakeBounds,
  parseTickerFilters,
  patchTickerFilters,
  previousRangeMissing,
  serializeTickerFilters,
} from './filters'
import { filterOptionsCascade, filterOptionsDataSource } from './tickerFilterCascade'
import {
  MembersIpMappingModal,
  MembersMappingModal,
} from './MembersMappingModal'
import { SmartMembersModal } from './SmartMembersModal'
import { TickerExcelModal } from './TickerExcelModal'
import { TickerMemberSearch } from './TickerMemberSearch'
import { TickerMoreFilters } from './TickerMoreFilters'
import { TmPicker } from './TmPicker'
import './bet-ticker.css'

const PREVIOUS_PRESETS: DateRangePresetId[] = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'last-7',
  'last-30',
  'custom',
]

/** Lab `/betfair-bet-ticker` max 15 days — drop last-30. */
const BETFAIR_PRESETS: DateRangePresetId[] = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'last-7',
  'custom',
]

/** Betfair line session: Odds Placed / Odds Matched hold line — Back ceil / Lay floor. */
export function tickerOddsPlaced(bet: TickerBet): string {
  if (isLineSessionBetListMarket(bet.marketName) && bet.oddsPlaced > 5) {
    return formatLineSessionDisplayRun(bet.oddsPlaced, bet.side) || odds(bet.oddsPlaced)
  }
  return odds(bet.oddsPlaced)
}

export function tickerOddsMatched(bet: TickerBet): string {
  if (isLineSessionBetListMarket(bet.marketName) && bet.oddsMatched > 5) {
    return formatLineSessionDisplayRun(bet.oddsMatched, bet.side) || odds(bet.oddsMatched)
  }
  return odds(bet.oddsMatched)
}

function llDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return y && m && d ? ll(new Date(y, m - 1, d)) : ll(iso)
}

function rangeSummary(from: string, to: string): string {
  if (!from || !to) return ''
  return from === to ? llDay(from) : `${llDay(from)} – ${llDay(to)}`
}

function presetOf(from: string, to: string): DateRangePresetId {
  for (const id of PREVIOUS_PRESETS) {
    const resolved = resolveRangePreset(id)
    if (resolved && resolved.from === from && resolved.to === to) return id
  }
  return 'custom'
}

function memberLabel(row: TickerBet): string {
  const name = row.memberUsername || row.memberId || '—'
  const code = row.currency?.trim()
  if (!code) return name
  return `${code.charAt(0).toUpperCase()} - ${name}`
}

function BetDetail({ bet }: { bet: TickerBet }) {
  const isLay = bet.side === 'LAY'
  return (
    <div className="w7-bm-bet">
      <article className="w7-bm-bet-hero" data-side={isLay ? 'lay' : 'back'}>
        <div className="w7-bm-bet-hero-rail" aria-hidden="true" />
        <div className="w7-bm-bet-hero-body">
          <div className="w7-bm-bet-chips" role="list">
            <span className="w7-bm-side-badge" data-side={isLay ? 'lay' : 'back'} role="listitem">
              {isLay ? 'Lay' : 'Back'}
            </span>
          </div>
          <p className="w7-bm-bet-event">{bet.eventName || '—'}</p>
          <p className="w7-bm-bet-market">{bet.marketName || '—'}</p>
          <p className="w7-bm-bet-selection">
            <span className="w7-bm-bet-selection-kicker">Selection</span>
            {bet.selection || '—'}
          </p>
          <div className="w7-bm-bet-metrics">
            <div className="w7-bm-bet-metric">
              <span className="w7-bm-bet-metric-label">Odds Placed</span>
              <strong className="w7-bm-bet-metric-value">
                {tickerOddsPlaced(bet)}
              </strong>
            </div>
            <div className="w7-bm-bet-metric">
              <span className="w7-bm-bet-metric-label">Odds Matched</span>
              <strong className="w7-bm-bet-metric-value">
                {tickerOddsMatched(bet)}
              </strong>
            </div>
          </div>
        </div>
      </article>
      <dl className="w7-bm-bet-facts">
        <div className="w7-bm-bet-fact">
          <dt>Matched</dt>
          <dd>{tickerAmt(bet.matchedAmount)}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>Unmatched</dt>
          <dd>{tickerAmt(bet.unmatchedAmount)}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>Member</dt>
          <dd>{memberLabel(bet)}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>Placed</dt>
          <dd>{bet.placedAt ? lll(bet.placedAt) : '—'}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>IP</dt>
          <dd>{bet.clientIp || '—'}</dd>
        </div>
      </dl>
    </div>
  )
}

export function BetTickerPage({
  mode = 'live',
  title,
  dataSource = 'pg',
}: {
  mode?: TickerAccessPage
  /** Override page chrome title (nav copies of an existing mode). */
  title?: string
  /** Forced list backend; Previous / Flag Final use Lab Betfair 7-day BQ switch. */
  dataSource?: 'pg' | 'bq' | 'flag-final-bq'
}) {
  const previous = mode === 'previous' || mode === 'flag-final'
  const flagFinal = mode === 'flag-final'
  const betfair = mode === 'betfair'
  const master = mode === 'master'
  const tickerSource = betfair ? ('betfair' as const) : ('sportsbook' as const)
  const rangeMaxDays = betfair ? BETFAIR_RANGE_MAX_DAYS : PREVIOUS_RANGE_MAX_DAYS
  const [params, setParams] = useSearchParams()
  const [selected, setSelected] = useState<TickerBet | null>(null)
  const [smartOpen, setSmartOpen] = useState(false)
  const [membersOpen, setMembersOpen] = useState(false)
  const [ipsOpen, setIpsOpen] = useState(false)
  const [commentBet, setCommentBet] = useState<TickerBet | null>(null)
  const [commentDraft, setCommentDraft] = useState('')
  const [excelOpen, setExcelOpen] = useState(false)
  const [excelPage, setExcelPage] = useState(1)
  const me = useCmMe()
  const manageSmart = Boolean(me.data) && canManageSmartMembers(me.data?.caller, mode)
  const manageMembersMapping =
    Boolean(me.data) && canManageMembersMapping(me.data?.caller, mode)
  const manageMembersIp =
    Boolean(me.data) && canManageMembersIpMapping(me.data?.caller, mode)
  const canFlag = Boolean(me.data) && canFlagTickerBet(me.data?.caller, mode)
  const canComment = Boolean(me.data) && canCommentTickerBet(me.data?.caller, mode)
  const canExcel = Boolean(me.data) && canExportTickerExcel(me.data?.caller)
  const toast = useToast()
  const flagM = useFlagBet()
  const excelGen = useTickerExcelGenerate()
  const filters = useMemo(() => parseTickerFilters(params), [params])
  /** Lab Betfair: BQ when span > 7 days or toDate ≠ today (Previous + Flag Final). */
  const resolvedDataSource: 'pg' | 'bq' | 'flag-final-bq' =
    mode === 'previous' || mode === 'flag-final'
      ? shouldUseTickerBq(filters.from, filters.to)
        ? mode === 'flag-final'
          ? 'flag-final-bq'
          : 'bq'
        : 'pg'
      : dataSource
  const needsRange = (previous || betfair) && previousRangeMissing(filters)
  /** First visit only — missing `flagged` means Lab default Final. Explicit `flagged=all` must stick. */
  const needsFinalDefault = flagFinal && !params.has('flagged')
  const tmsQ = useAllTm(true)
  const mappingQ = useBettickerMapping(true)
  const mapping = mappingQ.data
  const narrowed = filtersAreActive(filters)
  const modeLabel =
    title ??
    (mode === 'flag-final'
      ? 'Bet Ticker Flag Final'
      : mode === 'previous'
        ? 'Bet Ticker Previous'
        : mode === 'betfair'
          ? 'Betfair Bet Ticker'
          : mode === 'master'
            ? 'Master Bet Ticker'
            : 'Bet Ticker')

  const setFilters = (patch: Partial<TickerFilters>) => {
    const next = patchTickerFilters(filters, patch)
    const nextParams = serializeTickerFilters(next)
    // serialize omits flagged=all; keep it so needsFinalDefault does not bounce back to final
    if (flagFinal && next.flagged === 'all') nextParams.set('flagged', 'all')
    setParams(nextParams, { replace: true })
  }

  const resetFilters = () =>
    setFilters(
      flagFinal
        ? defaultFlagFinalFilters()
        : previous || betfair
          ? { ...DEFAULT_TICKER_FILTERS, ...defaultPreviousRange() }
          : DEFAULT_TICKER_FILTERS,
    )

  useEffect(() => {
    if (!needsRange && !needsFinalDefault) return
    const patch: Partial<TickerFilters> = {}
    if (needsRange) Object.assign(patch, defaultPreviousRange())
    if (needsFinalDefault) patch.flagged = 'final'
    setParams(serializeTickerFilters(patchTickerFilters(filters, patch)), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRange, needsFinalDefault])

  useEffect(() => {
    if (filters.status === 'all') return
    const allowed = tickerStatusesForScope(previous ? 'previous' : 'current')
    if (!(allowed as readonly string[]).includes(filters.status)) {
      setFilters({ status: 'all' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previous, filters.status])

  const ticker = useBookTicker({
    statusScope: previous ? 'previous' : 'current',
    source: tickerSource,
    dataSource: resolvedDataSource,
    filters,
    enabled: !needsRange,
  })
  const filterOptsDs = filterOptionsDataSource({
    previous,
    flagFinal,
    pageDataSource: resolvedDataSource,
  })
  const cascade = filterOptionsCascade({
    sport: filters.sport,
    event: filters.event,
  })
  const options = useBookTickerFilterOptions({
    statusScope: previous ? 'previous' : 'current',
    source: tickerSource,
    // Previous → tickerBQ pickers; Flag Final → tickerFlagFinalBQ pickers.
    dataSource: filterOptsDs,
    from: filters.from || undefined,
    to: filters.to || undefined,
    tmId: filters.tmId || undefined,
    eventType: cascade.eventType,
    eventName: cascade.eventName,
    enabled: !needsRange,
  })

  const page = ticker.data
  const rows = page?.rows ?? []
  const total = page?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / filters.pageSize) || 1)
  const safePage = Math.min(Math.max(1, filters.page), pageCount)
  const sports = options.data?.sports ?? []
  const events = options.data?.events ?? []
  const markets = useMemo(
    () => marketsMatchingType(options.data?.markets ?? [], filters.marketType),
    [options.data?.markets, filters.marketType],
  )
  const asOf = page?.asOf
  const fraudUsernames = useMemo(
    () => uniqueUsernames(rows.map((r) => r.memberUsername)),
    [rows],
  )
  const fraudQ = useFraudScores(fraudUsernames, flagFinal && rows.length > 0)
  const fraudMap = fraudQ.data
  const fraudLoading = fraudQ.isFetching

  useEffect(() => {
    if (needsRange || !ticker.isSuccess) return
    if (filters.page === safePage) return
    setFilters({ page: safePage })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRange, ticker.isSuccess, filters.page, safePage])

  const activeFilters = useMemo((): ActiveFilter[] => {
    const chips: ActiveFilter[] = []
    if (filters.tmId) {
      const tmName =
        (tmsQ.data ?? []).find((t) => t._id === filters.tmId)?.username || filters.tmId
      chips.push({
        id: 'tm',
        label: `TM: ${tmName}`,
        removeLabel: 'Clear TM filter',
        onRemove: () => setFilters({ tmId: '' }),
      })
    }
    for (const id of filters.members) {
      chips.push({
        id: `member-${id}`,
        label: `Member: ${id}`,
        removeLabel: `Remove member ${id}`,
        onRemove: () =>
          setFilters({ members: filters.members.filter((m) => m !== id) }),
      })
    }
    if (filters.search) {
      chips.push({
        id: 'q',
        label: `Member: ${filters.search}`,
        removeLabel: 'Remove member username filter',
        onRemove: () => setFilters({ search: '' }),
      })
    }
    if (filters.sport !== 'all') {
      chips.push({
        id: 'sport',
        label: `Sub Game: ${filters.sport}`,
        removeLabel: 'Remove sub game filter',
        onRemove: () => setFilters({ sport: 'all', event: '', market: '' }),
      })
    }
    if (filters.event) {
      chips.push({
        id: 'event',
        label: `Event: ${filters.event}`,
        removeLabel: 'Remove event filter',
        onRemove: () => setFilters({ event: '', market: '' }),
      })
    }
    if (filters.marketType !== 'all') {
      chips.push({
        id: 'mtype',
        label: `Market type: ${filters.marketType}`,
        removeLabel: 'Clear market type',
        onRemove: () => setFilters({ marketType: 'all', market: '' }),
      })
    }
    if (filters.market) {
      chips.push({
        id: 'market',
        label: `Market: ${filters.market}`,
        removeLabel: 'Remove market filter',
        onRemove: () => setFilters({ market: '' }),
      })
    }
    if (filters.currency !== 'all') {
      chips.push({
        id: 'ccy',
        label: `Currency: ${filters.currency}`,
        removeLabel: 'Clear currency filter',
        onRemove: () => setFilters({ currency: 'all' }),
      })
    }
    if (filters.stakeMin != null || filters.stakeMax != null) {
      const ordered = orderStakeBounds(filters.stakeMin, filters.stakeMax)
      const from = ordered.stakeMin != null ? String(ordered.stakeMin) : '…'
      const to = ordered.stakeMax != null ? String(ordered.stakeMax) : '…'
      chips.push({
        id: 'stake',
        label: `Stake: ${from} – ${to}`,
        removeLabel: 'Clear stake range',
        onRemove: () => setFilters({ stakeMin: null, stakeMax: null }),
      })
    }
    if (filters.side !== 'all') {
      chips.push({
        id: 'side',
        label: filters.side === 'BACK' ? 'Back only' : 'Lay only',
        removeLabel: 'Show back and lay',
        onRemove: () => setFilters({ side: 'all' }),
      })
    }
    if (filters.status !== 'all') {
      chips.push({
        id: 'status',
        label: `Status: ${tickerStatusLabel(filters.status)}`,
        removeLabel: 'Clear status filter',
        onRemove: () => setFilters({ status: 'all' }),
      })
    }
    if (filters.flagged === 'flagged') {
      chips.push({
        id: 'flagged',
        label: 'Flagged bets',
        removeLabel: 'Show all bets',
        onRemove: () => setFilters({ flagged: 'all' }),
      })
    }
    if (filters.flagged === 'final') {
      chips.push({
        id: 'flagged-final',
        label: 'Flagged Final Bets',
        removeLabel: 'Show all bets',
        onRemove: () => setFilters({ flagged: 'all' }),
      })
    }
    return chips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, previous, tmsQ.data])

  const onToggleFlag = (bet: TickerBet) => {
    if (!canFlag) return
    flagM.mutate(flagToggleRequest(bet), {
      onSuccess: (res) => toast.success(res.message || 'Updated'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not update flag'),
    })
  }

  const onFinalCategory = (bet: TickerBet, category: FinalFlagCategory) => {
    if (!canFlag) return
    flagM.mutate(flagFinalCategoryRequest(bet, category), {
      onSuccess: (res) => toast.success(res.message || 'Updated'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not update flag'),
    })
  }

  const onClearFinal = (bet: TickerBet) => {
    if (!canFlag) return
    flagM.mutate(flagClearFinalRequest(bet), {
      onSuccess: (res) => toast.success(res.message || 'Updated'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not update flag'),
    })
  }

  const onGenerateExcel = () => {
    if (!canExcel) return
    if (!filters.from || !filters.to) {
      toast.error('Select a date range first')
      return
    }
    excelGen.mutate(buildExcelGenerateRequest(filters), {
      onSuccess: (res) => toast.success(res.message || 'Excel generate queued'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not generate Excel'),
    })
  }

  const onOpenExcelList = () => {
    if (!canExcel) return
    setExcelPage(1)
    setExcelOpen(true)
  }

  const openComment = (bet: TickerBet) => {
    setCommentBet(bet)
    setCommentDraft(bet.comment)
  }

  const commentReady = isFlagCommentReady(commentDraft)

  const saveComment = () => {
    if (!canComment || !commentBet) return
    if (!isFlagCommentReady(commentDraft)) {
      toast.error('Comment is required')
      return
    }
    flagM.mutate(flagCommentRequest(commentBet, commentDraft), {
      onSuccess: (res) => {
        toast.success(res.message || 'Comment updated')
        setCommentBet(null)
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not save comment'),
    })
  }

  // Time only (24h + seconds) — date lives in range filters / detail drawer.
  const placedCell = (iso: string | null) =>
    iso ? <span className="w7-bm-placed w7-mono">{hms(iso)}</span> : '—'

  const columns: Column<TickerBet>[] = [
    {
      id: 'flag',
      header: 'Flag',
      cell: (row) => (
        <FlagCell
          bet={row}
          busy={flagM.isPending}
          canFlag={canFlag}
          canComment={canComment}
          finalMode={flagFinal}
          onToggle={onToggleFlag}
          onComment={openComment}
          onFinalCategory={onFinalCategory}
          onClearFinal={onClearFinal}
        />
      ),
    },
    {
      id: 'member',
      header: 'Member',
      cell: (row) => {
        // Lab tr ng-class: text-blue / text-warnings / text-danger / reasonColor on whole row;
        // marker class on Member drives CSS :has() row color.
        const flag = resolveTickerMemberFlag(row.memberUsername, row.clientIp, mapping, {
          smartUser: row.smartUser,
          isSmartUserByCron: row.isSmartUserByCron,
          doubtfull: row.doubtfull,
          watchlist: row.watchlist,
        })
        const flagClass =
          flag === 'smart'
            ? 'w7-bm-member--smart'
            : flag === 'doubtfull'
              ? 'w7-bm-member--doubtfull'
              : flag === 'watchlist'
                ? 'w7-bm-member--watchlist'
                : ''
        const reasonClass = betfairReasonRowClass({
          reason: row.reason,
          isFlagged: row.isFlagged,
          isBetfairFlagged: row.isBetfairFlagged,
          memberFlag: flag,
          smartUserComment: row.smartUserComment,
        })
        const memberClass = ['w7-bm-member', flagClass, reasonClass].filter(Boolean).join(' ')
        const cronComment =
          row.isSmartUserByCron && row.smartUserComment
            ? row.smartUserComment.trim()
            : ''
        const baseTitle = row.currency
          ? `${row.memberUsername || row.memberId} — ${row.currency}`
          : row.memberUsername || row.memberId || undefined
        const reasonDotClass = showBetfairReasonDot(row)
          ? betfairReasonDotClass(row.reason)
          : null
        return (
          <span
            className={memberClass}
            title={baseTitle}
            data-testid={row.isSmartUserByCron ? 'member-smart-cron' : undefined}
          >
            {cronComment ? (
              <span
                className="w7-bm-member-cron-icon"
                title={cronComment}
                data-testid="member-smart-cron-comment"
                aria-label={cronComment}
              >
                <Icon name="comment" size={14} />
              </span>
            ) : null}
            {reasonDotClass ? (
              <span
                className={`w7-bm-reason-dot ${reasonDotClass}`}
                title={row.reason}
                data-testid="betfair-reason-dot"
                aria-label={row.reason}
              />
            ) : null}
            {memberLabel(row)}
          </span>
        )
      },
      sortValue: (row) => row.memberUsername,
    },
    {
      id: 'when',
      header: 'Place time',
      cell: (row) => placedCell(row.placedAt),
      sortValue: (row) => row.placedAt ?? '',
    },
    {
      id: 'matchedAt',
      header: 'Matched time',
      cell: (row) => placedCell(row.matchedAt),
      sortValue: (row) => row.matchedAt ?? '',
    },
    {
      id: 'event',
      header: 'Event',
      cell: (row) => <span className="w7-bm-event-name">{row.eventName || '—'}</span>,
      sortValue: (row) => row.eventName,
    },
    {
      id: 'market',
      header: 'Market',
      cell: (row) => <span className="w7-bm-event-market">{row.marketName || '—'}</span>,
      sortValue: (row) => row.marketName,
    },
    {
      id: 'sel',
      header: 'Selection',
      cell: (row) => <span className="w7-bm-selection">{row.selection}</span>,
    },
    {
      id: 'side',
      header: 'Side',
      cell: (row) => (
        <Chip size="sm" tone={row.side === 'LAY' ? 'lay' : 'back'}>
          {row.side}
        </Chip>
      ),
    },
    {
      id: 'oddsPlaced',
      header: 'Odds Placed',
      numeric: true,
      cell: (row) => <span className="w7-bm-odds">{tickerOddsPlaced(row)}</span>,
      sortValue: (row) => row.oddsPlaced,
    },
    {
      id: 'oddsMatched',
      header: 'Odds Matched',
      numeric: true,
      cell: (row) => <span className="w7-bm-odds">{tickerOddsMatched(row)}</span>,
      sortValue: (row) => row.oddsMatched,
    },
    {
      id: 'matched',
      header: 'Matched',
      numeric: true,
      cell: (row) => tickerAmt(row.matchedAmount),
      sortValue: (row) => row.matchedAmount,
    },
    {
      id: 'unmatched',
      header: 'Unmatched',
      numeric: true,
      cell: (row) => tickerAmt(row.unmatchedAmount),
      sortValue: (row) => row.unmatchedAmount,
    },
    {
      id: 'pl',
      header: 'Profit/Liability',
      numeric: true,
      cell: (row) => (
        <span data-tone={row.side === 'LAY' ? 'loss' : 'profit'}>
          {indianCurrency(row.profitLiability, 2)}
        </span>
      ),
      sortValue: (row) => row.profitLiability,
    },
    // Lab Master Bet Ticker omits Betfair amount columns.
    ...(!master
      ? ([
          {
            id: 'bfPlaced',
            header: 'Amount Placed on Betfair',
            numeric: true,
            cell: (row: TickerBet) => indianCurrency(row.amountPlacedOnBetfair, 0),
            sortValue: (row: TickerBet) => row.amountPlacedOnBetfair,
          },
          {
            id: 'bfExecuted',
            header: 'Betfair Executed',
            numeric: true,
            cell: (row: TickerBet) => indianCurrency(row.amountExecuted, 0),
            sortValue: (row: TickerBet) => row.amountExecuted,
          },
          {
            id: 'bfPending',
            header: 'Betfair Pending',
            numeric: true,
            cell: (row: TickerBet) => indianCurrency(row.amountPending, 0),
            sortValue: (row: TickerBet) => row.amountPending,
          },
          {
            id: 'bfCancelled',
            header: 'Betfair Cancelled',
            numeric: true,
            cell: (row: TickerBet) => indianCurrency(row.amountCancelled, 0),
            sortValue: (row: TickerBet) => row.amountCancelled,
          },
        ] as Column<TickerBet>[])
      : []),
    {
      id: 'ip',
      header: 'IP',
      cell: (row) => <span className="w7-mono">{row.clientIp || '—'}</span>,
      sortValue: (row) => row.clientIp ?? '',
    },
    {
      id: 'multiplier',
      header: 'Multiplier',
      numeric: true,
      cell: (row) => String(row.multiplier || 1),
      sortValue: (row) => row.multiplier,
    },
    ...(flagFinal
      ? ([
          {
            id: 'trust',
            header: 'Trust Indicator',
            cell: (row: TickerBet) => {
              if (fraudLoading) {
                return <span className="w7-bm-score-loading" aria-label="Loading">…</span>
              }
              const score = fraudMap?.get(row.memberUsername.trim().toLowerCase())
              const tone = trustIndicatorTone(score?.trustScore)
              if (tone === 'none') return '—'
              return (
                <span
                  className="w7-bm-trust-dot"
                  data-tone={tone}
                  title={String(score?.trustScore)}
                  aria-label={`Trust ${score?.trustScore}`}
                />
              )
            },
          },
          {
            id: 'playerScore',
            header: 'Score',
            numeric: true,
            cell: (row: TickerBet) => {
              if (fraudLoading) {
                return <span className="w7-bm-score-loading" aria-label="Loading">…</span>
              }
              const score = fraudMap?.get(row.memberUsername.trim().toLowerCase())
              return formatPlayerScore(score?.playerScore)
            },
          },
        ] as Column<TickerBet>[])
      : []),
  ]

  return (
    <div className="w7-bm w7-bm--ticker">
      <PageHead
        title={
          <span className="w7-bm-title">
            <span className="w7-bm-title-workspace">Exposure</span>
            <span className="w7-bm-title-sep" aria-hidden="true">
              /
            </span>
            {modeLabel}
          </span>
        }
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setMembersOpen(true)}
              data-testid="members-name-btn"
            >
              Members Name
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIpsOpen(true)}
              data-testid="members-ip-btn"
            >
              Members Ip
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setSmartOpen(true)}
              data-testid="smart-members-btn"
            >
              Smart Members
            </Button>
          </>
        }
      />

      <div className="w7-bm-body">
        <div className="w7-bm-ticker" data-testid="ticker-workspace">
          <div className="w7-bm-toolbar">
            <FilterBar
              label={`${modeLabel} filters`}
              active={activeFilters}
              onClearAll={narrowed ? resetFilters : undefined}
              status={
                narrowed
                  ? `Showing ${total} bets with ${activeFilters.length} filters applied`
                  : `Showing ${total} bets`
              }
            >
              {flagFinal ? (
                <div
                  className="w7-bm-toolbar-excel"
                  role="group"
                  aria-label="Excel export"
                  hidden
                >
                  <Button
                    type="button"
                    variant="secondary"
                    busy={excelGen.isPending}
                    disabled={!canExcel || needsRange}
                    title={canExcel ? undefined : 'Generate Excel (no access)'}
                    data-testid="ticker-excel-generate-btn"
                    onClick={onGenerateExcel}
                  >
                    Generate Excel
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={!canExcel}
                    title={canExcel ? undefined : 'Download Excel (no access)'}
                    data-testid="ticker-excel-download-btn"
                    onClick={onOpenExcelList}
                  >
                    Download Excel
                  </Button>
                </div>
              ) : null}
              {/* Row 1: multi member search · live + refresh */}
              <div className="w7-bm-toolbar-primary">
                <TickerMemberSearch
                  value={filters.members}
                  onChange={(members) => setFilters({ members })}
                  canClear={filters.members.length > 0 || !!filters.search}
                  onClear={() => setFilters({ members: [], search: '' })}
                />
                <span className="w7-bm-toolbar-actions">
                  <span className="w7-bm-toolbar-meta" aria-live="polite">
                    {previous ? (
                      <Chip size="sm" tone="muted" icon="clock">
                        Settled
                      </Chip>
                    ) : (
                      <span className="w7-bm-live">
                        <Dot tone="info" pulse />
                        <span>Live</span>
                      </span>
                    )}
                    {!needsRange && !ticker.isError ? (
                      <span className="w7-bm-toolbar-count">
                        {ticker.isLoading && total === 0 ? '…' : total.toLocaleString('en-IN')}
                      </span>
                    ) : null}
                    {asOf && !previous ? (
                      <span className="w7-bm-toolbar-asof" title={lll(asOf)}>
                        {ago(asOf)}
                      </span>
                    ) : null}
                  </span>
                  <Button
                    variant="ghost"
                    icon="refresh"
                    busy={ticker.isFetching}
                    aria-label="Refresh"
                    onClick={() => void ticker.refetch()}
                  >
                    Refresh
                  </Button>
                </span>
              </div>
              {/* Row 2: TM + side + status + Flags + More */}
              <div className="w7-bm-toolbar-filters">
                {!master ? (
                  <TmPicker
                    value={filters.tmId}
                    options={(tmsQ.data ?? []).map((tm) => ({ id: tm._id, username: tm.username }))}
                    onChange={(tmId) => setFilters({ tmId })}
                    loading={tmsQ.isLoading}
                  />
                ) : null}
                <Segmented
                  label="Side"
                  value={filters.side}
                  onChange={(side) => setFilters({ side })}
                  items={[
                    { id: 'all', label: 'All' },
                    { id: 'BACK', label: 'Back' },
                    { id: 'LAY', label: 'Lay' },
                  ]}
                />
                <select
                  className="w7-bm-select w7-bm-select--status"
                  aria-label="Status"
                  value={filters.status}
                  onChange={(e) => setFilters({ status: e.target.value })}
                >
                  <option value="all">All status</option>
                  {tickerStatusesForScope(previous ? 'previous' : 'current').map((status) => (
                    <option key={status} value={status}>
                      {tickerStatusLabel(status)}
                    </option>
                  ))}
                </select>
                {flagFinal ? (
                  <select
                    className="w7-bm-select"
                    aria-label="Flag"
                    value={filters.flagged}
                    onChange={(e) => {
                      const v = e.target.value
                      setFilters({
                        flagged: v === 'flagged' || v === 'final' ? v : 'all',
                      })
                    }}
                  >
                    <option value="all">All flags</option>
                    <option value="flagged">Flagged bets</option>
                    <option value="final">Flagged Final Bets</option>
                  </select>
                ) : null}
                {previous || betfair ? (
                  <DateRangePicker
                    label="Date range"
                    preset={presetOf(filters.from, filters.to)}
                    value={{ from: filters.from, to: filters.to }}
                    summary={rangeSummary(filters.from, filters.to)}
                    presets={betfair ? BETFAIR_PRESETS : PREVIOUS_PRESETS}
                    maxSpanDays={rangeMaxDays}
                    onChange={(next) => setFilters({ from: next.from, to: next.to })}
                  />
                ) : null}
                <TickerMoreFilters
                  filters={filters}
                  setFilters={setFilters}
                  sports={sports}
                  events={events}
                  markets={markets}
                  showFlagsFilter={!flagFinal}
                />
              </div>
            </FilterBar>
            <div className="w7-bm-smart-legend" aria-label="Bet ticker flag legend">
              {BETFAIR_REASON_LEGEND.map(({ reason, color }) => (
                <span key={reason} className="w7-bm-smart-legend-item">
                  <span
                    className="w7-bm-smart-dot"
                    style={{ background: color }}
                    aria-hidden="true"
                  />
                  {reason}
                </span>
              ))}
              {(
                [
                  ['watchlist', 'Watchlist Members'],
                  ['doubtfull', 'Doubtfull Members'],
                  ['smart', 'Smart Members'],
                ] as const
              ).map(([key, label]) => (
                <span key={key} className="w7-bm-smart-legend-item">
                  <span
                    className="w7-bm-smart-dot"
                    style={{ background: TICKER_FLAG_COLORS[key] }}
                    aria-hidden="true"
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {needsRange ? (
            <StateBlock icon="clock" title="Choose a date range">
              {betfair
                ? `Betfair bets are read for a date range of up to ${BETFAIR_RANGE_MAX_DAYS} days.`
                : `Previous bets are read for a date range of up to ${PREVIOUS_RANGE_MAX_DAYS} days.`}
            </StateBlock>
          ) : ticker.isError ? (
            <StateBlock
              icon="warning"
              title="Could not load bets"
              tone="error"
              actions={
                <Button variant="secondary" onClick={() => void ticker.refetch()}>
                  Try again
                </Button>
              }
            >
              {ticker.error instanceof Error
                ? ticker.error.message
                : 'The bet service did not answer.'}
            </StateBlock>
          ) : ticker.isLoading && rows.length === 0 ? (
            <div className="w7-bm-results">
              <div className="w7-bm-table-wrap">
                <DataTable
                  label={modeLabel}
                  rows={[]}
                  columns={columns}
                  rowKey={(row) => row.id}
                  density="compact"
                  stickyOffset={0}
                  loading
                  empty={null}
                />
              </div>
            </div>
          ) : total === 0 ? (
            <div className="w7-bm-empty-panel" data-testid="ticker-empty">
              <StateBlock
                icon={narrowed ? 'filter' : 'activity'}
                title={
                  narrowed
                    ? 'No bets match these filters'
                    : previous
                      ? 'No settled bets in this range'
                      : 'No open bets right now'
                }
                actions={
                  <>
                    {narrowed ? (
                      <Button variant="primary" icon="close" onClick={resetFilters}>
                        Clear filters
                      </Button>
                    ) : null}
                    <Button
                      variant="secondary"
                      icon="refresh"
                      busy={ticker.isFetching}
                      onClick={() => void ticker.refetch()}
                    >
                      Refresh
                    </Button>
                  </>
                }
              >
                {narrowed
                  ? 'Widen the search, clear a TM or date, or reset filters to see the full ticker again.'
                  : previous
                    ? 'Try a wider date range (up to 31 days) or pick another member.'
                    : 'When members place open sportsbook bets, they will show up here newest first.'}
              </StateBlock>
            </div>
          ) : (
            <div className="w7-bm-results">
              <div className="w7-bm-table-wrap">
                <DataTable
                  label={modeLabel}
                  rows={rows}
                  columns={columns}
                  rowKey={(row) => row.id}
                  density="compact"
                  stickyOffset={0}
                  loading={ticker.isFetching && rows.length === 0}
                  onRowClick={setSelected}
                  empty={null}
                />
              </div>
              <div className="w7-bm-pager">
                <select
                  className="w7-bm-select"
                  aria-label="Rows per page"
                  value={String(filters.pageSize)}
                  onChange={(e) => setFilters({ pageSize: Number(e.target.value) })}
                >
                  {TICKER_PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </select>
                <Pager
                  page={safePage}
                  pageCount={pageCount}
                  total={total}
                  pageSize={filters.pageSize}
                  onChange={(next) => setFilters({ page: next })}
                />
              </div>
            </div>
          )}

          <Drawer
            open={Boolean(selected)}
            onClose={() => setSelected(null)}
            title={selected ? selected.memberUsername || selected.memberId : 'Bet'}
            subtitle={
              selected
                ? `${selected.side === 'LAY' ? 'Lay' : 'Back'}${
                    selected.placedAt ? ` · ${ago(selected.placedAt)}` : ''
                  }`
                : undefined
            }
          >
            {selected ? <BetDetail bet={selected} /> : null}
          </Drawer>

          <MembersMappingModal
            open={membersOpen}
            onClose={() => setMembersOpen(false)}
            canEdit={manageMembersMapping}
          />
          <MembersIpMappingModal
            open={ipsOpen}
            onClose={() => setIpsOpen(false)}
            canEdit={manageMembersIp}
          />
          <SmartMembersModal
            open={smartOpen}
            onClose={() => setSmartOpen(false)}
            canEdit={manageSmart}
          />
          {flagFinal ? (
            <TickerExcelModal
              open={excelOpen}
              onClose={() => setExcelOpen(false)}
              page={excelPage}
              onPage={setExcelPage}
            />
          ) : null}

          <Modal
            open={Boolean(commentBet)}
            onClose={() => {
              if (flagM.isPending) return
              setCommentBet(null)
            }}
            title="Bet comment"
            subtitle={
              commentBet
                ? `${commentBet.memberUsername || commentBet.memberId} · ${commentBet.eventName || '—'}`
                : undefined
            }
            closeDisabled={flagM.isPending}
            footer={
              <div className="w7-bm-flag-modal-actions">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={flagM.isPending}
                  onClick={() => setCommentBet(null)}
                >
                  {canComment ? 'Cancel' : 'Close'}
                </Button>
                {canComment ? (
                  <Button
                    type="button"
                    busy={flagM.isPending}
                    disabled={!commentReady || flagM.isPending}
                    onClick={saveComment}
                  >
                    Save
                  </Button>
                ) : null}
              </div>
            }
          >
            {!canComment ? (
              <p className="w7-bm-smart-modal-hint" data-testid="comment-readonly">
                View only — comment Access Level is off for this page.
              </p>
            ) : null}
            <Field label="Comment" required={canComment}>
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  aria-required={canComment}
                  rows={4}
                  maxLength={FLAG_COMMENT_MAX}
                  value={commentDraft}
                  readOnly={!canComment}
                  onChange={(e) => setCommentDraft(normalizeFlagComment(e.target.value))}
                  placeholder={canComment ? 'Enter comment' : 'No comment'}
                />
              )}
            </Field>
          </Modal>
        </div>
      </div>
    </div>
  )
}

export function BetTickerLivePage() {
  return <BetTickerPage mode="live" />
}

export function BetTickerPreviousPage() {
  return <BetTickerPage mode="previous" />
}

export function BetTickerFlagFinalPage() {
  return <BetTickerPage mode="flag-final" />
}

export function BetTickerBetfairPage() {
  return <BetTickerPage mode="betfair" />
}

export function BetTickerMasterPage() {
  return <BetTickerPage mode="master" />
}
